import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Flag to prevent multiple simultaneous refresh token calls
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Try to get token from both 'token' and 'accessToken' for compatibility
        const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor with automatic token refresh
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If error is 401 and we haven't retried yet
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers && token) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return api(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Get refresh token from localStorage or cookie (backend uses cookie)
                const refreshToken = localStorage.getItem('refreshToken');

                if (!refreshToken) {
                    // No refresh token, logout user
                    localStorage.removeItem('token');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    processQueue(error, null);
                    window.location.href = '/login';
                    return Promise.reject(error);
                }

                // Call refresh token endpoint
                // Backend uses cookies, but we also send in body as fallback
                const response = await axios.post(
                    `${API_URL}/auth/refresh-token`,
                    { refreshToken }, // Send in body as fallback (backend checks both cookies and body)
                    {
                        withCredentials: true, // Important: send cookies
                    }
                );

                // Backend returns: { success: true, data: { user, token } }
                const newToken = response.data.data?.token || response.data?.token || response.data?.data?.accessToken;

                if (!newToken) {
                    throw new Error('No token received from refresh endpoint');
                }

                // Update tokens in localStorage
                localStorage.setItem('token', newToken);
                localStorage.setItem('accessToken', newToken);

                // Update authStore if available
                try {
                    const { useAuthStore } = await import('@/store/authStore');
                    const store = useAuthStore.getState();
                    store.setUser(response.data.data?.user || null);
                } catch {
                    // Ignore if authStore is not available
                }

                // Update authorization header for the original request
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }

                // Process queued requests
                processQueue(null, newToken);

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh token failed, logout user
                processQueue(refreshError as AxiosError, null);
                localStorage.removeItem('token');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default api;
